import React, { useContext, useState, useEffect } from 'react';
import { cloneDeep } from 'lodash';
import { Box, Checkbox, Tooltip, Typography } from '@mui/material';
import { assign } from 'xstate';
import { IntlShape } from 'react-intl';
import StarIcon from '@mui/icons-material/Star';

import { MachineContext } from '../../MachineContext';
import { BaseComponent } from '../Component';
import { SendContext } from '../../SendContext';
import { translateWithFallback } from '../../util';
import { MultipleInput } from '../MultipleInput';
import { theme as evoachDefaultTheme } from '../../theme/theme';
import { EvoachButton } from '../Button';

export interface EditableMultiselectComponent extends BaseComponent {
  type: 'editableMultiselect';
  props: EditableMultiselectProps;
}
export interface EditableMultiselectItem {
  type: string;
  value: string;
  starred?: boolean;
}
export interface EditableMultiselectProps {
  elements: EditableMultiselectItem[][];
  maxItems?: number;
  minItems?: number;
  buttonText: string;
  saveResultTo: string;
  hideOwnInput?: boolean;
  presetValues?: string[];
  placeholderTexts?: string[];
  selectAll?: boolean;
  toolTipWarningMessage?: string;
}

// a little function to help us with reordering the result

const selectItems = (
  list: EditableMultiselectItem[],
  selectAll: boolean | undefined
): EditableMultiselectItem[] => {
  let retlist = list;

  if (selectAll) {
    retlist = list.map((listentry: any) => {
      if (typeof listentry === 'object') {
        listentry.starred = true;
      }
      return listentry;
    });
  }
  return retlist;
};

const starredItems = (list: EditableMultiselectItem[]): number => {
  let result = 0;
  list.forEach((value) => {
    if (value.starred) result++;
  });
  return result;
};

export const EditableMultiselect: React.FC<EditableMultiselectProps> = ({
  elements,
  maxItems,
  minItems,
  buttonText,
  saveResultTo,
  presetValues = [''],
  placeholderTexts = ['Write here'],
  hideOwnInput,
  selectAll,
  toolTipWarningMessage = '',
}) => {
  const { send } = useContext(SendContext);
  const [items, setItems] = useState<EditableMultiselectItem[]>(
    selectItems(elements.flat(), selectAll)
  );
  const [snippets, setSnippets] = useState<string[]>([]);
  const [warning, setWarning] = useState<string>('');
  const checkTooltip = (index: number) => {
    const newItems = cloneDeep(items);
    if (newItems[index].starred) {
      setWarning('');
    } else if (maxItems) {
      if (maxItems <= starredItems(items) + snippets.length) {
        setWarning(toolTipWarningMessage);
      } else {
        setWarning('');
      }
    }
  };
  const checkSnippetTooltip = (index: number, snippetLength: number) => {
    if (index == snippetLength - 1 && maxItems) {
      if (maxItems <= starredItems(items) + snippets.length) {
        setWarning(toolTipWarningMessage);
      } else {
        setWarning('');
      }
    } else if (maxItems) {
      setWarning('');
    }
  };

  useEffect(() => {
    setItems(selectItems(elements.flat(), selectAll));
  }, [elements, selectAll]);

  return (
    <div>
      <section
        style={{ display: 'flex', flexDirection: 'row', flexWrap: 'wrap' }}
      >
        {items.map((item, index) => {
          return (
            <Tooltip
              key={`${item.type}-${item.value}-${index}`}
              title={warning == null ? '' : warning}
              onMouseEnter={() => {
                checkTooltip(index);
              }}
            >
              <Box
                key={`${item.type}-${item.value}-${index}`}
                sx={{
                  display: 'flex',
                  flexDirection: 'row',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  padding: '0.5rem',
                  userSelect: 'none',
                  margin: index % 2 !== 0 ? '0 0 8px 0' : '0 8px 8px 0',
                  borderRadius: '6px',
                  backgroundColor: item.starred
                    ? evoachDefaultTheme.palette.secondary.main
                    : '#d1edf3',
                  width: 'calc((100% - 44px )/ 2)',
                }}
              >
                <Checkbox
                  sx={{
                    width: 0,
                    marginRight: '0.5rem',
                  }}
                  checkedIcon={<StarIcon sx={{ color: '#FFFFFF' }} />}
                  icon={<StarIcon />}
                  checked={!!item.starred}
                  onClick={() => {
                    const newItems = cloneDeep(items);
                    newItems[index].starred = !newItems[index].starred;
                    setItems(newItems);
                  }}
                />
                <Typography color={!item.starred ? '#000000' : '#FFFFFF'}>
                  {item.value}
                </Typography>
                <div></div>
              </Box>
            </Tooltip>
          );
        })}
      </section>
      {!hideOwnInput ? (
        <MultipleInput
          buttonText="next"
          saveResultTo="x"
          showButton={false}
          placeholderTexts={placeholderTexts}
          presetValues={presetValues}
          toolTipCallback={(index: number, snippetLength: number) =>
            checkSnippetTooltip(index, snippetLength)
          }
          warning={warning}
          onChange={(snippets: string[]) => {
            snippets = snippets.filter((snip) => snip && snip.trim() !== '');
            setSnippets(snippets);
          }}
        />
      ) : (
        ''
      )}
      <EvoachButton
        disabled={
          (minItems !== undefined &&
            minItems > starredItems(items) + snippets.length) ||
          (maxItems !== undefined &&
            maxItems <= starredItems(items) + snippets.length - 1)
        }
        onClick={() => {
          const starredItems = items.filter((item) => item.starred);
          let snippetItems: EditableMultiselectItem[] = [];
          for (let i = 0; i < snippets.length; i++) {
            if (snippets[i] !== '') {
              snippetItems.push({
                type: 'editableMultiselectOwnInput',
                value: snippets[i],
                starred: true,
              });
            }
          }
          send({
            type: 'writeToContext',
            payload: {
              [saveResultTo]: starredItems.concat(snippetItems),
            },
          });
          send({ type: 'next' });
        }}
      >
        {buttonText}
      </EvoachButton>
    </div>
  );
};

export const renderEditableMultiselectAction = (intl: IntlShape) => {
  return assign((context: MachineContext, _event, actionMetadata) => {
    const newContext = cloneDeep(context);
    newContext.widgetData = newContext.widgetData.filter(
      (element) => !element.temporary
    );
    const payload = actionMetadata.action.payload;
    const translatedplaceholdertexts = payload.placeholderTexts.map(
      (transkey: string) => {
        return translateWithFallback(intl, transkey);
      }
    );

    // we expect payload elements to be an array of objects,
    let payloadElements = payload.elements;
    // if elements are passed take them as payloadElements

    // if there are no  payload.elements (i.e. they are undefined or []),
    // then check whether elements are passed via getLabelsFrom
    // note: elements may be an array of arrays of elements!
    // note: testing for payloadElements===[] is not working!
    if (
      (!payloadElements ||
        (Array.isArray(payloadElements) && payloadElements.length === 0)) &&
      payload.getLabelsFrom
    ) {
      payloadElements = context.userData[payload.getLabelsFrom];
    }

    // ensure that elements are managed via objects, not matter whether
    // payloadelements contains an array of objects or strings (e.g. by getLabelsFrom)
    if (payloadElements) {
      payloadElements = payloadElements.map((element: any) => {
        if (typeof element === 'string') {
          return {
            type: 'editablemultiselectelement',
            value: translateWithFallback(intl, element),
            starred: false,
          };
        } else {
          return {
            type: element.type,
            value: translateWithFallback(intl, element.value),
            starred: !!element.starred,
          };
        }
      });
    } else {
      payloadElements = [];
    }

    // getValuesFrom overwrites elements
    const dataSourceList = payload.getValuesFrom;

    // if payloadElements exists and datasourcelist exists, then we have to merge both
    const elements =
      dataSourceList && dataSourceList.length > 0
        ? dataSourceList.map((dataSource: string) => {
            const contextData = context.userData[dataSource];

            // when payloadELements exists and is not empty
            if (
              payloadElements &&
              Array.isArray(payloadElements) &&
              payloadElements.length > 0
            ) {
              const fromStrings: string[] = [];
              const mappedData = payloadElements.map((element: any) => {
                const valueExists = contextData
                  ? contextData.reduce(
                      (exists: any, mappedElement: any) => {
                        if (typeof mappedElement === 'string') {
                          //
                          if (mappedElement === element.value) {
                            // remember to delete this value when combining at the end
                            fromStrings.push(element.value);
                            return {
                              exist: true,
                              starred: true,
                              type: element.type, // not correct as strings don't come out of ...
                            };
                          } else {
                            return exists;
                          }
                        } else {
                          if (mappedElement.value === element.value) {
                            return {
                              exist: true,
                              starred: mappedElement.starred,
                              type: element.type,
                            };
                          } else {
                            return exists;
                          }
                        }
                      },
                      { exist: false, starred: false, type: element.type }
                    )
                  : { exist: false, starred: false, type: element.type };

                return {
                  type: valueExists.type,
                  value: element.value,
                  starred: valueExists.exist
                    ? valueExists.starred
                    : element.starred,
                };
              });

              // now find all elements that are in mappedData but not in
              // payloadElements ==> these are the user inputs
              let userInputs = [];

              if (contextData) {
                if (typeof contextData[0] === 'string') {
                  // If the values in getValuesFrom var are a string array, then create new entries
                  // If they were matched with payloadElements, then they were remembered in fromStrings.
                  // If that's tha case, we have to filter them out as they would have been added twice
                  userInputs = contextData
                    .filter((elem: string) => !fromStrings.includes(elem))
                    .map((elem: string) => {
                      return {
                        type: 'editableMultiselectGetValueFromString',
                        value: elem,
                        starred: true,
                      };
                    });
                } else {
                  // if the values in getValuesFrom are an object (e.g., from a previous control), filter own
                  userInputs = contextData.filter(
                    (element: any) =>
                      element.type === 'editableMultiselectOwnInput'
                  );
                }
              }

              return [...mappedData, ...userInputs];
            } else {
              // not payloadElements !!! take anything you get as new elements with starred = true
              if (
                contextData &&
                Array.isArray(contextData) &&
                contextData.length > 0
              ) {
                if (typeof contextData[0] === 'string') {
                  const mappedStringArray = contextData.map((elem: string) => {
                    return {
                      type: 'editableMultiselectGetValueFromString',
                      value: elem,
                      starred: false, // PROD-1277
                    };
                  });
                  return mappedStringArray;
                } else {
                  // if contextData is not an array of strings, it is an array of objects
                  // then return as it is
                  return contextData;
                }
              } else {
                return [];
              }
            }
          })
        : [payloadElements]; // add array here because control expects array of arrays of objects

    // add prepared data to widgetdata
    newContext.widgetData.push({
      type: 'editableMultiselect',
      temporary: actionMetadata.action.temporary ?? false,
      props: {
        elements: elements,
        saveResultTo: payload.saveResultTo,
        buttonText: translateWithFallback(intl, payload.buttonText),
        maxItems: payload.maxItems,
        minItems: payload.minItems,
        placeholderTexts: translatedplaceholdertexts,
        presetValues: payload.presetValues,
        hideOwnInput: payload.hideOwnInput,
        selectAll: payload.selectAll ?? false, // PROD-1277
        toolTipWarningMessage: translateWithFallback(
          intl,
          payload.toolTipWarningMessage
        ),
      },
    });
    return newContext;
  });
};
